import React from 'react';
import { Badge, Button, Modal, ProgressBar, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faRotateRight } from '@fortawesome/free-solid-svg-icons';

import { ConciliationApi } from '../../Services/ConciliationApiDataService';

const pick = (obj, keys, fallback = null) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return fallback;
};

const statusVariant = (status) => {
  const value = (status ?? '').toString().toLowerCase();
  if (value === 'succeeded' || value === 'completed' || value === 'success') return 'success';
  if (value === 'failed') return 'danger';
  if (value === 'running') return 'primary';
  if (value === 'queued' || value === 'pending') return 'warning';
  if (value === 'notfound' || value === 'skipped') return 'secondary';
  return 'secondary';
};

const isActiveStatus = (status) => ['queued', 'running'].includes((status ?? '').toString().toLowerCase());

const apiErrorMessage = (e, fallback) => {
  const data = e?.response?.data;
  if (typeof data === 'string') return data;
  return data?.message ?? data?.Message ?? data?.title ?? data?.Title ?? fallback;
};

const plannedActionText = (action, index) => {
  if (typeof action === 'string') return action;
  if (!action || typeof action !== 'object') return `Action ${index + 1}`;
  return pick(action, ['name', 'Name', 'action', 'Action', 'actionType', 'ActionType', 'description', 'Description'], `Action ${index + 1}`);
};

const ProgressSummary = ({ run }) => {
  const totalRows = Number(pick(run, ['totalRows', 'TotalRows'], 0)) || 0;
  const succeededRows = Number(pick(run, ['succeededRows', 'SucceededRows'], 0)) || 0;
  const failedRows = Number(pick(run, ['failedRows', 'FailedRows'], 0)) || 0;
  const notFoundRows = Number(pick(run, ['notFoundRows', 'NotFoundRows'], 0)) || 0;
  const completedRows = succeededRows + failedRows + notFoundRows;
  const percent = totalRows > 0 ? Math.min(100, Math.round((completedRows / totalRows) * 100)) : 0;

  return (
    <div>
      <ProgressBar now={percent} label={`${percent}%`} className="mb-2" />
      <div className="d-flex flex-wrap gap-2 small">
        <Badge bg="light" text="dark" className="border">Total: {totalRows}</Badge>
        <Badge bg="warning" text="dark">Pending: {pick(run, ['pendingRows', 'PendingRows'], 0)}</Badge>
        <Badge bg="primary">Running: {pick(run, ['runningRows', 'RunningRows'], 0)}</Badge>
        <Badge bg="success">Succeeded: {succeededRows}</Badge>
        <Badge bg="danger">Failed: {failedRows}</Badge>
        <Badge bg="secondary">Not found: {notFoundRows}</Badge>
      </div>
    </div>
  );
};

const ResultRows = ({ items }) => (
  <div className="table-responsive mt-3">
    <Table size="sm" bordered hover className="mb-0 align-middle">
      <thead className="table-light">
        <tr>
          <th>Type</th>
          <th>Conciliation Key</th>
          <th>Status</th>
          <th>Message</th>
          <th>Error</th>
        </tr>
      </thead>
      <tbody>
        {(items ?? []).length === 0 && (
          <tr>
            <td colSpan={5} className="text-muted text-center py-3">No row results yet.</td>
          </tr>
        )}
        {(items ?? []).map((item, index) => {
          const status = pick(item, ['status', 'Status'], '-');
          const type = pick(item, ['conciliationType', 'ConciliationType'], '-');
          const typeLabel = type?.toString().toLowerCase() === 'rel' ? 'Rel' : type?.toString().toLowerCase() === 'gastos' ? 'Gastos' : type;
          return (
            <tr key={pick(item, ['conciliationKey', 'ConciliationKey'], index)}>
              <td>{typeLabel}</td>
              <td>{pick(item, ['conciliationKey', 'ConciliationKey'], '-')}</td>
              <td><Badge bg={statusVariant(status)}>{status}</Badge></td>
              <td>{pick(item, ['message', 'Message'], '')}</td>
              <td className="text-danger">{pick(item, ['error', 'Error'], '')}</td>
            </tr>
          );
        })}
      </tbody>
    </Table>
  </div>
);

const WorkbenchSuggestionsPanel = ({
  conciliationType,
  gastosFilter,
  relFilter,
  currentUser,
  getToken,
  onFinished,
  companyCanExecute = false,
  buttonClassName = 'btn btn-sm btn-outline-success'
}) => {
    const readOnly = !companyCanExecute;
  const [show, setShow] = React.useState(false);
  const [preview, setPreview] = React.useState(null);
  const [run, setRun] = React.useState(null);
  const [history, setHistory] = React.useState([]);
  const [loadingPreview, setLoadingPreview] = React.useState(false);
  const [starting, setStarting] = React.useState(false);
  const [resuming, setResuming] = React.useState(false);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [error, setError] = React.useState('');
  const finishedRunIdsRef = React.useRef(new Set());

  const filterPayload = React.useMemo(() => ({
    conciliationType,
    gastosFilter: conciliationType === 'rel' ? null : gastosFilter,
    relFilter: conciliationType === 'gastos' ? null : relFilter
  }), [conciliationType, gastosFilter, relFilter]);

  const loadHistory = React.useCallback(async () => {
    try {
      setLoadingHistory(true);
      const token = await getToken();
      const res = await ConciliationApi.listWorkbenchSuggestionRuns({ conciliationType, page: 1, pageSize: 20 }, token);
      setHistory(res?.items ?? res?.Items ?? []);
    } catch (e) {
      console.error('Failed loading suggestion history', e);
    } finally {
      setLoadingHistory(false);
    }
  }, [conciliationType, getToken]);

  const openPreview = async () => {
    setShow(true);
    setError('');
    setPreview(null);
    try {
      setLoadingPreview(true);
      const token = await getToken();
      const data = await ConciliationApi.previewWorkbenchSuggestions(filterPayload, token);
      setPreview(data);
      await loadHistory();
    } catch (e) {
      console.error('Suggestion preview failed', e);
      setError('Could not load suggestion preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  const startRun = async () => {
        if (readOnly) { return; }
    try {
      setStarting(true);
      setError('');
      const token = await getToken();
      const data = await ConciliationApi.startWorkbenchSuggestions({
        requestedBy: currentUser || 'unknown',
        scope: filterPayload
      }, token);
      const runId = pick(data, ['runId', 'RunId', 'id', 'Id']);
      const initialRun = runId ? { ...data, runId } : data;
      setRun(initialRun);
      await loadHistory();
    } catch (e) {
      console.error('Suggestion start failed', e);
      setError('Could not start suggestion generation.');
    } finally {
      setStarting(false);
    }
  };

  const resumeRun = async () => {
        if (readOnly) { return; }
    const runId = pick(run, ['runId', 'RunId', 'id', 'Id']);
    if (!runId) return;

    try {
      setResuming(true);
      setError('');
      const token = await getToken();
      const data = await ConciliationApi.resumeWorkbenchSuggestionRun(runId, token);
      const resumedRunId = pick(data, ['runId', 'RunId', 'id', 'Id']);
      setRun(resumedRunId ? data : { ...data, runId });
      await loadHistory();
    } catch (e) {
      console.error('Suggestion resume failed', e);
      setError(apiErrorMessage(e, 'Could not resume suggestion run.'));
    } finally {
      setResuming(false);
    }
  };

  React.useEffect(() => {
    const runId = pick(run, ['runId', 'RunId', 'id', 'Id']);
    const status = pick(run, ['status', 'Status']);
    if (!runId || (status && !isActiveStatus(status))) return undefined;

    const intervalId = window.setInterval(async () => {
      try {
        const token = await getToken();
        const data = await ConciliationApi.getWorkbenchSuggestionRun(runId, token);
        setRun(data);
      } catch (e) {
        console.error('Suggestion poll failed', e);
      }
    }, 2500);

    return () => window.clearInterval(intervalId);
  }, [getToken, run]);

  React.useEffect(() => {
    const runId = pick(run, ['runId', 'RunId', 'id', 'Id']);
    const status = pick(run, ['status', 'Status']);
    if (!runId || !status || isActiveStatus(status) || finishedRunIdsRef.current.has(runId)) return;

    finishedRunIdsRef.current.add(runId);
    loadHistory();
    onFinished?.(run);
  }, [loadHistory, onFinished, run]);

  const plannedActions = pick(preview, ['plannedActions', 'PlannedActions'], []);
  const runId = pick(run, ['runId', 'RunId', 'id', 'Id']);
  const runStatus = pick(run, ['status', 'Status']);
  const runItems = pick(run, ['items', 'Items'], []);

  return (
    <>
      <button className={buttonClassName} onClick={openPreview} disabled={loadingPreview || (conciliationType !== 'rel' && !gastosFilter) || (conciliationType !== 'gastos' && !relFilter)}>
        <FontAwesomeIcon icon={faLightbulb} className="me-1" />
        Generate suggestions
      </button>

      <Modal show={show} onHide={() => setShow(false)} size="xl" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Generate suggestions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger py-2">{error}</div>}

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Preview</h6>
              {loadingPreview && <Spinner animation="border" size="sm" />}
            </div>
            {preview ? (
              <>
                <div className="mb-2">
                  <Badge bg="light" text="dark" className="border">
                    Total rows: {pick(preview, ['totalRows', 'TotalRows'], 0)}
                  </Badge>
                </div>
                {(plannedActions ?? []).length > 0 ? (
                  <ul className="mb-0">
                    {plannedActions.map((action, index) => (
                      <li key={index}>{plannedActionText(action, index)}</li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-muted small">No planned actions returned.</div>
                )}
              </>
            ) : !loadingPreview ? (
              <div className="text-muted small">No preview loaded.</div>
            ) : null}
          </div>

          {run && (
            <div className="border rounded p-3 mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <div>
                  <strong>Run:</strong> {runId ?? '-'}
                </div>
                <div className="d-flex align-items-center gap-2">
                  {isActiveStatus(runStatus) && (
                    <Button variant="outline-primary" size="sm" onClick={resumeRun} disabled={readOnly || (resuming)}>
                      {resuming ? <Spinner animation="border" size="sm" className="me-2" /> : null}
                      Resume run
                    </Button>
                  )}
                  <Badge bg={statusVariant(runStatus)}>{runStatus ?? '-'}</Badge>
                </div>
              </div>
              <ProgressSummary run={run} />
              <ResultRows items={runItems} />
            </div>
          )}

          <div className="border rounded p-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">Recent runs</h6>
              <Button variant="outline-secondary" size="sm" onClick={loadHistory} disabled={loadingHistory}>
                <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                Refresh
              </Button>
            </div>
            <div className="table-responsive">
              <Table size="sm" hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Run</th>
                    <th>Status</th>
                    <th>Total</th>
                    <th>Succeeded</th>
                    <th>Failed</th>
                    <th>Not found</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory && (
                    <tr><td colSpan={6} className="text-muted text-center py-3">Loading...</td></tr>
                  )}
                  {!loadingHistory && history.length === 0 && (
                    <tr><td colSpan={6} className="text-muted text-center py-3">No recent runs.</td></tr>
                  )}
                  {!loadingHistory && history.map((item, index) => {
                    const status = pick(item, ['status', 'Status'], '-');
                    return (
                      <tr key={pick(item, ['runId', 'RunId', 'id', 'Id'], index)}>
                        <td>{pick(item, ['runId', 'RunId', 'id', 'Id'], '-')}</td>
                        <td><Badge bg={statusVariant(status)}>{status}</Badge></td>
                        <td>{pick(item, ['totalRows', 'TotalRows'], 0)}</td>
                        <td>{pick(item, ['succeededRows', 'SucceededRows'], 0)}</td>
                        <td>{pick(item, ['failedRows', 'FailedRows'], 0)}</td>
                        <td>{pick(item, ['notFoundRows', 'NotFoundRows'], 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShow(false)}>Close</Button>
          <Button variant="success" onClick={startRun} disabled={readOnly || (!preview || starting || isActiveStatus(runStatus))}>
            {starting ? <Spinner animation="border" size="sm" className="me-2" /> : null}
            Confirm and start
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default WorkbenchSuggestionsPanel;
